import * as React from "react";

import { useAnalytics, useNavigation } from "@/providers";
import {
  RowActionsMenu,
  type RowAction,
} from "@/shared/components/RowActionsMenu";
import {
  type ActionReference,
  type InvocationRecord,
  InvocationStatus,
  isDataSourceSpecified,
} from "@/shared/domain/actions";
import { type OrgRecord } from "@/shared/domain/orgs";
import { Ban, Copy } from "@/shared/foundation/icons";
import { ConfirmDialog } from "@/shared/foundation/primitives";

import useCancellationCallback from "./useCancellationCallback";

const TERMINAL_STATUSES = new Set([
  InvocationStatus.Completed,
  InvocationStatus.Cancelled,
  InvocationStatus.Failed,
  InvocationStatus.Deadly,
]);

interface InvocationRowMenuProps {
  invocation: InvocationRecord;
  org: OrgRecord | null;
  /** Called once a cancel succeeds, so the list can refetch. */
  onCancel: () => void;
}

/**
 * An invocation row's "…" menu: Clone opens the invoke page seeded from the
 * run; Cancel, offered while the run is still in flight, confirms first.
 */
export function InvocationRowMenu({
  invocation,
  org,
  onCancel,
}: InvocationRowMenuProps) {
  const { goto } = useNavigation();
  const { trackEvent } = useAnalytics();
  const [confirmingCancel, setConfirmingCancel] = React.useState(false);
  const [cancelling, setCancelling] = React.useState(false);
  const cancelInvocation = useCancellationCallback(invocation, org, onCancel);

  const latest = invocation.status[invocation.status.length - 1]?.status;
  const isTerminal = latest !== undefined && TERMINAL_STATUSES.has(latest);

  const clone = () => {
    trackEvent("InvocationCloned", { id: invocation.invocation_id });
    const action = invocation.provenance.action;
    const actionRef: ActionReference = {
      name: action.name,
      digest: action.digest,
      owner: org?.org_id !== action.org_id ? action.org_id : undefined,
    };
    goto.invokeAction({
      action: actionRef,
      datasetId: isDataSourceSpecified(invocation.data_source)
        ? invocation.data_source.data_source_id
        : undefined,
      baseInvocation: invocation.invocation_id,
    });
  };

  // The confirm stays open, its buttons busy, until the request settles.
  const confirmCancel = async () => {
    setCancelling(true);
    try {
      await cancelInvocation();
    } finally {
      setCancelling(false);
      setConfirmingCancel(false);
    }
  };

  const actions: RowAction[] = [
    { label: "Clone", icon: <Copy />, onClick: clone },
  ];
  if (!isTerminal) {
    actions.push({
      label: "Cancel",
      icon: <Ban />,
      danger: true,
      onClick: () => {
        trackEvent("CancelInvocationClicked", {
          invocationId: invocation.invocation_id,
        });
        setConfirmingCancel(true);
      },
    });
  }

  return (
    <>
      <RowActionsMenu label={invocation.invocation_id} actions={actions} />
      <ConfirmDialog
        open={confirmingCancel}
        onOpenChange={(open) => {
          if (!cancelling) {
            setConfirmingCancel(open);
          }
        }}
        title="Cancel invocation?"
        description={`This stops the run of ${invocation.provenance.action.name}.`}
        confirmLabel="Cancel invocation"
        cancelLabel="Keep running"
        destructive
        pending={cancelling}
        onConfirm={() => void confirmCancel()}
      />
    </>
  );
}
